import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useQueryClient } from 'react-query';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import styled from '@emotion/styled';

import BasicModal from '@components/_atoms/Modal/Basic';
import ProductTable from './ProductTable';
import SearchProductCard from '@components/_organism/service/SearchProductCard';
import { Toast } from '@components/_atoms/Toast';

import { getCurrentItem } from '../Hooks/getCurrentItem';
import { getExistMaterialIdx } from '../Hooks/isExistMaterial';
import { usePreventModalScroll } from '@hooks/interaction/usePreventModalScroll';
import { useGetUserSaleItemsFetch, usePatchUserSaleItemsFetch } from '@hooks/fetch/useUserFetch';

import { TypoVariant } from '@constants/atoms';
import { AlarmProductType } from '../../SellerPromoteAlramContent/types';
import {
  SALE_PRODUCT_SEARCH_FIELD,
  deleteSaleProductListState,
  saleProductAlarmIsUserSaleMaterialPushState,
  saleProductOptionAtom,
} from '@recoil/service/SaleProductAlarm';

type Props = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
};

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 880px;
  overflow: auto;
`;

export default function ProductAlarmModal({ isOpen, onClose }: Props) {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const ko = i18n.language === 'ko';

  const { userSaleItemsFetchData, userSaleItemsFetchIsLoading } = useGetUserSaleItemsFetch();
  const { patchUserSaleItemsFetchMutate } = usePatchUserSaleItemsFetch();

  // recoil state
  const saleProductAlarmIsUserSaleMaterialPush = useRecoilValue(saleProductAlarmIsUserSaleMaterialPushState);
  const [deleteSaleProductList, setDeleteSaleProductList] = useRecoilState(deleteSaleProductListState);
  const [saleProductTypeOption] = useRecoilState(saleProductOptionAtom);

  // recoil reset
  const resetSaleProductOption = useResetRecoilState(saleProductOptionAtom);
  const resetDeleteSaleProductListState = useResetRecoilState(deleteSaleProductListState);
  const resetSaleProductAlarmIsUserSaleMaterialPush = useResetRecoilState(saleProductAlarmIsUserSaleMaterialPushState);

  // alarm modify state
  const [addedSaleItemList, setAddedSaleItemList] = useState<AlarmProductType[]>([]);
  const [userSaleItemList, setUserSaleItemList] = useState<AlarmProductType[]>([]);
  
  const productList = useMemo(() => {
    return [...addedSaleItemList, ...userSaleItemList];
  }, [userSaleItemList, addedSaleItemList]);

  const handleDeleteAlarmProduct = (index: number) => {
    const isAddedSaleItem = index < addedSaleItemList.length;
    const productIndex = isAddedSaleItem ? index : index - addedSaleItemList.length;

    //index는 전체 리스트의 index,
    if (isAddedSaleItem) {
      const filteredAddedSaleItemList = addedSaleItemList.filter((saleItem, index) => {
        return index !== productIndex;
      });
      setAddedSaleItemList(filteredAddedSaleItemList);
    } else {
      const targetUserSaleItem = userSaleItemList[productIndex];
      const filteredUserSaleItemList = userSaleItemList.filter((saleItem, index) => {
        return index !== productIndex;
      });
      setUserSaleItemList(filteredUserSaleItemList);
      setDeleteSaleProductList((prev) => [...prev, targetUserSaleItem.id!]);
    }
  };

  const handleClickReset = () => {
    setDeleteSaleProductList([]);
    setAddedSaleItemList([]);
    setUserSaleItemList([...(userSaleItemsFetchData as AlarmProductType[])]);
  };

  const handleClickMaterial = (material: string, field?: SALE_PRODUCT_SEARCH_FIELD) => {
    if (!field) return;
    const existItemIdx = getExistMaterialIdx(material, field, productList, saleProductTypeOption.searchCondition);

    if (existItemIdx > -1) {
      handleDeleteAlarmProduct(existItemIdx);
    } else {
      const currentItem = getCurrentItem(material, field, saleProductTypeOption.searchCondition);
      setAddedSaleItemList((prev) => [...prev, currentItem]);
    }
  };

  const handleClickSave = () => {
    const createSaleMaterialList = addedSaleItemList.map((addedSaleItem) => {
      const { product_type, category, type1, material, standard_code } = addedSaleItem;
      return {
        product_type,
        standard_code: standard_code,
        category: category ?? undefined,
        type1,
        material,
      };
    });

    patchUserSaleItemsFetchMutate(
      {
        is_user_sale_material_push: saleProductAlarmIsUserSaleMaterialPush,
        delete_sale_material_list: deleteSaleProductList,
        create_sale_material_list: createSaleMaterialList,
      },
      {
        onSuccess: () => {
          Toast.success(ko ? '변경 내용을 저장했어요' : 'Changes have been saved');

          queryClient.invalidateQueries('getSaleQuotationList');
          queryClient.invalidateQueries('getSaleQuotationListCount');

          onClose();
        },
      },
    );
  };

  useEffect(() => {
    if (!userSaleItemsFetchIsLoading) {
      setUserSaleItemList(userSaleItemsFetchData as AlarmProductType[]);
    }
  }, [userSaleItemsFetchIsLoading]);

  //페이지 unmount시 state reset
  useEffect(() => {
    return () => {
      resetSaleProductAlarmIsUserSaleMaterialPush();
      resetDeleteSaleProductListState();
      resetSaleProductOption();
    };
  }, []);

  usePreventModalScroll();

  return (
    <BasicModal
      title={ko ? '알림 신청하기' : 'Set up material request notifications'}
      subTitle={
        ko
          ? '판매가 가능한 자재를 선택해 주세요.\n선택한 자재가 필요한 프로젝트가 등록되면 놓치지 않게 알려드릴게요.'
          : 'Please select the materials that you are able to sell.\nWhen the materials you have selected are required or requested for a project, we will notify you to ensure you do not miss out.'
      }
      variant={TypoVariant.SH2}
      isOpen={isOpen}
      ok={handleClickSave}
      okText={ko ? '설정완료' : 'Set up complete'}
      onClose={onClose}
      width={720}
      padding={40}>
      <Layout>
        <SearchProductCard
          isShowAlarmSetter={false}
          productList={productList}
          handleClickMaterial={handleClickMaterial}
        />
        <ProductTable
          productList={productList}
          handleClickReset={handleClickReset}
          onClose={handleDeleteAlarmProduct}
        />
      </Layout>
    </BasicModal>
  );
}
